import * as React from 'react';
import { ColumnDef, HeaderContext } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { navigate } from 'wouter/use-location';
import { useQueryClient } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import { ICmdTableCustomerCell, ICmdTableUserCell, TFuelCardRecords } from './interfaces';
import {
  FUEL_CARD_ASSIGNED_TO_KEY,
  FUEL_CARD_CARD_NUMBER_KEY,
  FUEL_CARD_CREATED_AT_KEY,
  FUEL_CARD_CREATED_BY_KEY,
  FUEL_CARD_CUSTOMER_ID_KEY,
  FUEL_CARD_DELETED_AT_KEY,
  FUEL_CARD_DETAIL_KEY,
  FUEL_CARD_IDENTIFIER_KEY,
  FUEL_CARD_MODIFIED_AT_KEY,
  FUEL_CARD_MODIFIED_BY_KEY,
  FUEL_CARD_NOTE_KEY,
  FUEL_CARD_PROVIDER_ID_KEY,
  FUEL_CARD_QUERY_KEY,
  FUEL_CARD_TABLE_KEY,
  FUEL_CARD_VALID_UNTIL_KEY,
} from './constants';
import ApiService from '../../Services/ApiService';
import { TABLE_FUEL_CARD } from '../../Services/TableService';
import { URL_FUEL_CARDS } from '../../router/constants';
// import * as CmdTableState from '../CmdTable/CmdTableState';
import ThSearch from '../CmdTable/TableHeadCell/ThSearch';
import { ICmdTableColumn, ICmdTableFilter } from '../CmdTable/types';
import CustomerCell from '../CmdTable/TableCells/CustomerCell';
import DateTimeCell from '../CmdTable/TableCells/DateTimeCell';
import ThOnlySort from '../CmdTable/TableHeadCell/ThOnlySort';
import ThSimpleSelect from '../CmdTable/TableHeadCell/ThSimpleSelect';
import ThMultiSelect from '../CmdTable/TableHeadCell/ThMultiSelect';
import ThDateTime from '../CmdTable/TableHeadCell/ThDateTime';
import TextCell from '../CmdTable/TableCells/TextCell';
import RemoveAction from './RemoveAction';
import DetailAction from '../CmdTable/TableActions/DetailAction';
import { selectedCustomersAtom } from '../../store/recoil/customers';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
} from '../CmdTable/CmdTableState';
import useAnalytics from '../../hooks/useAnalytics';

interface IUseFuelCards {
  columns: ColumnDef<TFuelCardRecords>[];
  columnOrder: string[];
  columnVisibility: { [key: string]: boolean };
}

export default function useFuelCards(): IUseFuelCards {
  const { formatMessage: f } = useIntl();
  const queryClient = useQueryClient();
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(FUEL_CARD_TABLE_KEY)
  );
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(FUEL_CARD_TABLE_KEY)
  );
  const columnsTable = useRecoilValue<ICmdTableColumn[]>(columnsAtom);
  const { trackEvent } = useAnalytics();

  const updateTable = React.useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [FUEL_CARD_TABLE_KEY, filterForRequest, sortFilter, selectedCustomers],
    });
  }, [queryClient, filterForRequest, sortFilter, selectedCustomers]);

  const handleRemove = React.useCallback(
    async (data: TFuelCardRecords) => {
      const response = await ApiService.put(
        `/v1/${TABLE_FUEL_CARD}/${data.id}/remove?customerId=${data.customerId.id}`,
        { id: data.id },
        true
      );
      if (response.data && response.data.status === 'ok') {
        updateTable();
        trackEvent(['fuel cards table', 'click', 'delete action']);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateTable]
  );

  const handleAction = (id: number, customerId?: number | number[]) => {
    if (customerId && !Array.isArray(customerId)) {
      navigate(`${URL_FUEL_CARDS}/edit/${id}?customerId=${customerId}`);
      trackEvent(['fuel cards table', 'click', 'detail action']);
    }
  };

  const columns = React.useMemo<ColumnDef<TFuelCardRecords>[]>(
    () => [
      {
        accessorKey: FUEL_CARD_CUSTOMER_ID_KEY,
        header: (_table: HeaderContext<TFuelCardRecords, unknown>) => (
          <ThSimpleSelect
            tableName={FUEL_CARD_TABLE_KEY}
            id={FUEL_CARD_CUSTOMER_ID_KEY}
            title={f({ id: `table_list.columns.${FUEL_CARD_CUSTOMER_ID_KEY}` })}
            tooltip={f({ id: `tables.column.fuelCard.header.customerId` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.customerId ? (
            <CustomerCell customer={info.getValue<ICmdTableCustomerCell>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 150,
      },
      {
        accessorKey: FUEL_CARD_PROVIDER_ID_KEY,
        header: (_table: HeaderContext<TFuelCardRecords, unknown>) => (
          <ThSimpleSelect
            tableName={FUEL_CARD_TABLE_KEY}
            id={FUEL_CARD_PROVIDER_ID_KEY}
            title={f({
              id: `table_list.columns.${FUEL_CARD_QUERY_KEY}.${FUEL_CARD_PROVIDER_ID_KEY}`,
            })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.fuelCardProviderId ? (
            <TextCell text={info.getValue<ICmdTableUserCell>().name || ''} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 200,
      },
      // {
      //   accessorKey: FUEL_CARD_COLOR_KEY,
      //   header: (table) => (
      //     <ThOnlySort
      //       tableName={FUEL_CARD_TABLE_KEY}
      //       id={FUEL_CARD_COLOR_KEY}
      //       title={f({ id: `table_list.columns.parkingTags.${FUEL_CARD_COLOR_KEY}` })}
      //       // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_FUEL_CARD_COLOR_KEY}.tooltip` })}
      //       isSortable={true}
      //     />
      //   ),
      //   cell: (info) =>
      //     info.row.original?.color ? (
      //       <ColorCell color={info.getValue<string>()} />
      //     ) : (
      //       <TextCell text="" />
      //     ),
      //   size: 80,
      // },
      {
        accessorKey: FUEL_CARD_CARD_NUMBER_KEY,
        header: (_table: HeaderContext<TFuelCardRecords, unknown>) => (
          <ThSearch
            tableName={FUEL_CARD_TABLE_KEY}
            id={FUEL_CARD_CARD_NUMBER_KEY}
            title={f({
              id: `table_list.columns.${FUEL_CARD_QUERY_KEY}.${FUEL_CARD_CARD_NUMBER_KEY}`,
            })}
            tooltip={f({ id: `tables.column.fuelCard.header.cardNumber` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.cardNumber ? (
            <TextCell text={info.getValue<string>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 180,
      },
      {
        accessorKey: FUEL_CARD_IDENTIFIER_KEY,
        header: (_table: HeaderContext<TFuelCardRecords, unknown>) => (
          <ThSearch
            tableName={FUEL_CARD_TABLE_KEY}
            id={FUEL_CARD_IDENTIFIER_KEY}
            title={f({
              id: `table_list.columns.${FUEL_CARD_QUERY_KEY}.${FUEL_CARD_IDENTIFIER_KEY}`,
            })}
            tooltip={f({ id: `form.inputs.fuelCards.identifier.placeholder` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.identifier ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 180,
      },
      {
        accessorKey: FUEL_CARD_VALID_UNTIL_KEY,
        header: (_table: HeaderContext<TFuelCardRecords, unknown>) => (
          <ThDateTime
            id={FUEL_CARD_VALID_UNTIL_KEY}
            tableName={FUEL_CARD_TABLE_KEY}
            title={f({
              id: `table_list.columns.${FUEL_CARD_QUERY_KEY}.${FUEL_CARD_VALID_UNTIL_KEY}`,
            })}
            tooltip={f({ id: `tables.column.fuelCard.header.validUntil` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.validUntil ? (
            <DateTimeCell datetime={info.getValue<string | null>()} format="D.M.YYYY" />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: FUEL_CARD_ASSIGNED_TO_KEY,
        header: (_table: HeaderContext<TFuelCardRecords, unknown>) => (
          <ThOnlySort
            tableName={FUEL_CARD_TABLE_KEY}
            id={FUEL_CARD_ASSIGNED_TO_KEY}
            title={f({
              id: `table_list.columns.${FUEL_CARD_QUERY_KEY}.${FUEL_CARD_ASSIGNED_TO_KEY}`,
            })}
            tooltip={f({ id: `tables.column.fuelCard.header.vehicleId` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.assignedTo ? (
            <TextCell text={info.getValue<string>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 180,
      },
      {
        accessorKey: FUEL_CARD_NOTE_KEY,
        header: (_table: HeaderContext<TFuelCardRecords, unknown>) => (
          <ThSearch
            tableName={FUEL_CARD_TABLE_KEY}
            id={FUEL_CARD_NOTE_KEY}
            title={f({ id: `table_list.columns.${FUEL_CARD_QUERY_KEY}.${FUEL_CARD_NOTE_KEY}` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.note ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 180,
      },
      {
        accessorKey: FUEL_CARD_CREATED_BY_KEY,
        header: (_table: HeaderContext<TFuelCardRecords, unknown>) => (
          <ThMultiSelect
            tableName={FUEL_CARD_TABLE_KEY}
            id={FUEL_CARD_CREATED_BY_KEY}
            title={f({ id: `table_list.columns.${FUEL_CARD_CREATED_BY_KEY}` })}
            tooltip={f({ id: `table_list.columns.createdBy.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.createdBy ? (
            <TextCell text={info.getValue<ICmdTableUserCell>().name || ''} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: FUEL_CARD_CREATED_AT_KEY,
        header: (_table: HeaderContext<TFuelCardRecords, unknown>) => (
          <ThDateTime
            tableName={FUEL_CARD_TABLE_KEY}
            id={FUEL_CARD_CREATED_AT_KEY}
            title={f({ id: `table_list.columns.${FUEL_CARD_CREATED_AT_KEY}` })}
            tooltip={f({ id: `table_list.columns.createdAt.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.createdAt ? (
            <DateTimeCell datetime={info.getValue<string | null>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: FUEL_CARD_MODIFIED_BY_KEY,
        header: (_table: HeaderContext<TFuelCardRecords, unknown>) => (
          <ThMultiSelect
            tableName={FUEL_CARD_TABLE_KEY}
            id={FUEL_CARD_MODIFIED_BY_KEY}
            title={f({ id: `table_list.columns.${FUEL_CARD_MODIFIED_BY_KEY}` })}
            tooltip={f({ id: `table_list.columns.modifiedBy.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.modifiedBy ? (
            <TextCell text={info.getValue<ICmdTableUserCell>().name || ''} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: FUEL_CARD_MODIFIED_AT_KEY,
        header: (_table: HeaderContext<TFuelCardRecords, unknown>) => (
          <ThDateTime
            id={FUEL_CARD_MODIFIED_AT_KEY}
            tableName={FUEL_CARD_TABLE_KEY}
            title={f({ id: `table_list.columns.${FUEL_CARD_MODIFIED_AT_KEY}` })}
            tooltip={f({ id: `table_list.columns.modifiedAt.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.modifiedAt ? (
            <DateTimeCell datetime={info.getValue<string | null>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },

      {
        accessorKey: FUEL_CARD_DETAIL_KEY,
        header: () => (
          <div className="w-sm text-center th-sticky sticky-col detail p-2">
            {f({ id: 'table.detailBtn' })}
          </div>
        ),
        cell: (info) =>
          info.row.original ? (
            <DetailAction
              id={info.row.original.id}
              tableName={TABLE_FUEL_CARD}
              customerId={info.row.original.customerId.id}
              onClickCallback={handleAction}
            />
          ) : (
            ''
          ),
        size: 60, // Adjusted for consistency with header styling
        enablePinning: true,
      },
      {
        accessorKey: FUEL_CARD_DELETED_AT_KEY,
        header: () => (
          <div className="w-sm text-center th-sticky sticky-col detail p-2">
            {f({ id: 'table.deleteBtn' })}
          </div>
        ),
        cell: (info) =>
          info.row.original ? (
            <RemoveAction data={info.row.original} handleRemove={handleRemove} />
          ) : (
            ''
          ),
        size: 80, // Adjusted for consistency with header styling
        enablePinning: true,
      },
    ],
    [f, handleRemove]
  );

  const columnOrder = React.useMemo(() => {
    const order = columnsTable.map((item) => item.key);
    // add the detail and delete columns to the end
    order.push(FUEL_CARD_DETAIL_KEY, FUEL_CARD_DELETED_AT_KEY);
    return order;
  }, [columnsTable]);

  const columnVisibility: { [index: string]: boolean } = React.useMemo(() => {
    return columnsTable.reduce((acc: { [index: string]: boolean }, column: ICmdTableColumn) => {
      acc[column.key] = !column.hidden;
      return acc;
    }, {});
  }, [columnsTable]);

  return {
    columns,
    columnOrder,
    columnVisibility,
  };
}
